import { useLazyQuery, useQuery } from '@apollo/client';
import { IonRouterLink } from '@ionic/react';
import equal from 'deep-equal';
import { ComponentProps, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { SwiperSlide } from 'swiper/react';

import BackButton from '@components/buttons/BackButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import BasicCard from '@components/cards/BasicCard';
import GoalTemplateFilters from '@components/forms/GoalTemplateFilters';
import HandleQuery from '@components/HandleQuery';
import styles from '@components/libraries/Library.module.css';
import GoalTemplatesLoader from '@components/loaders/GoalTemplatesLoader';
import Slides from '@components/Slides';
import BodyCopy from '@components/typography/BodyCopy';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Heading1 from '@components/typography/Heading1';
import Heading2 from '@components/typography/Heading2';
import useCreateGoal from '@hooks/mutations/useCreateGoal';
import useMyClient from '@hooks/useMyClient';
import useNodesFromConnection from '@hooks/useNodesFromConnection';
import useScrollPosition from '@hooks/useScrollPosition';
import useSearchGoalTemplates from '@hooks/useSearchGoalTemplates';
import { ScrollPositionPageTypeEnum } from '@typing/Enums';
import {
  FocusAreaEnum,
  GetGoalTemplatesBasicDocument,
  GetGoalTemplatesBasicQuery,
  GetGoalTemplatesBasicQueryVariables,
  GetJourneyWithPlanDocument,
  GetJourneyWithPlanQuery,
  GetJourneyWithPlanQueryVariables,
  GoalTemplateKindEnum,
  GoalTemplateSortByEnum,
  SortDirectionEnum
} from '@typing/Generated';
import { imageUrl } from '@utils/imageUtils';
import { groupByFocusArea } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Filters = ComponentProps<typeof GoalTemplateFilters>['filters'];

type Journey = GetJourneyWithPlanQuery['journey'];

type Props = {
  journeyId: string;
};

type GoalTemplate = NonNullable<
  NonNullable<NonNullable<GetGoalTemplatesBasicQuery['goalTemplates']['edges']>[number]>['node']
>;

type SectionProps = {
  focusArea: FocusAreaEnum;
  goalTemplates: GoalTemplate[];
  handleGoalCreate: (templateId: string) => void;
  journey: Journey;
  loadingGoalTemplateId: string | null;
};

const FocusAreaSection = ({
  focusArea,
  goalTemplates,
  handleGoalCreate,
  journey,
  loadingGoalTemplateId
}: SectionProps) => {
  const intl = useIntl();
  const myClient = useMyClient();

  /* eslint-disable @typescript-eslint/naming-convention */
  const breakPoints = {
    1: {
      slidesPerView: 1.1
    },
    1300: {
      slidesPerView: 3
    },
    1301: {
      slidesPerView: 4
    },

    600: {
      slidesPerView: 1.1
    },
    767: {
      slidesPerView: 2
    },
    950: {
      slidesPerView: 3
    }
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  return (
    <div className={styles.section}>
      <div className={styles.focusAreaSection}>
        <Heading1>
          <FormattedMessage id={`models.focusArea.${focusArea}`} />
        </Heading1>
      </div>

      <div className={styles.goalTemplates}>
        <Slides breakpoints={breakPoints} spaceBetween={20}>
          <SwiperSlide>
            <div className={styles[focusArea] + ' ' + styles.introCard}>
              <div className="align-center">
                <img
                  alt={intl.formatMessage({ id: `accessibility.altTags.${focusArea}` })}
                  className={styles.focusAreaImg}
                  src={imageUrl(focusArea)}
                />
              </div>

              <div className={styles.introDescription}>
                <div className={styles.introTitle}>
                  <BodyCopyBold color="shadesWhite">
                    <FormattedMessage id={`models.focusArea.${focusArea}`} />
                  </BodyCopyBold>
                </div>
                {focusArea !== FocusAreaEnum.under_eighteen && (
                  <BodyCopy color="shadesWhite">
                    <FormattedMessage id={`models.focusArea.descriptions.${focusArea}`} />
                  </BodyCopy>
                )}
              </div>
            </div>
          </SwiperSlide>
          {goalTemplates.map(goalTemplate => (
            <SwiperSlide key={goalTemplate.id}>
              {/* eslint-disable-next-line react/forbid-elements */}
              <IonRouterLink
                routerLink={journeyRoute({
                  journeyId: journey.id,
                  linkAfterJourney: `/care-plan/goal-templates/${goalTemplate.id}`,
                  myClient
                })}
              >
                <div className={styles.cardContent}>
                  <BasicCard hover="normal">
                    <div className={styles.spacing}>
                      <Heading2>{goalTemplate.title}</Heading2>
                      <div>
                        <div className={styles.buttonSpacing}>
                          {journey.goals.some(goal => goal.goalTemplate?.id === goalTemplate.id) ? (
                            <div className={styles.added}>
                              <FormattedMessage id="pages.goalTemplates.added" />
                            </div>
                          ) : (
                            <PrimaryButton
                              disabled={
                                journey.goals.some(goal => goal.goalTemplate?.id === goalTemplate.id) ||
                                loadingGoalTemplateId === goalTemplate.id
                              }
                              icon={'add'}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleGoalCreate(goalTemplate.id);
                              }}
                              textKey={'pages.goalTemplates.addToCarePlan'}
                            />
                          )}
                        </div>

                        <div className="align-center">
                          <TertiaryButton textKey="pages.goalTemplates.goalDetails" />
                        </div>
                      </div>
                    </div>
                  </BasicCard>
                </div>
              </IonRouterLink>
            </SwiperSlide>
          ))}
        </Slides>
      </div>
    </div>
  );
};

const GoalTemplatesLibrary = ({ journeyId }: Props) => {
  const [getGoalTemplates, getGoalTemplatesQuery] = useLazyQuery<
    GetGoalTemplatesBasicQuery,
    GetGoalTemplatesBasicQueryVariables
  >(GetGoalTemplatesBasicDocument);

  const getJourneyQuery = useQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>(
    GetJourneyWithPlanDocument,
    {
      variables: { id: journeyId }
    }
  );

  const [loadingId, setLoadingId] = useState<string | null>(null);
  const myClient = useMyClient();

  const createGoal = useCreateGoal({ journey: getJourneyQuery.data?.journey ?? null });

  const handleGoalCreate = (goalTemplateId: string) => {
    if (getJourneyQuery.data?.journey) {
      setLoadingId(goalTemplateId);
      createGoal({
        draft: myClient ? false : true,
        goalTemplateId,
        journeyId: getJourneyQuery.data.journey.id
      }).then(() => {
        setLoadingId(null);
      });
    }
  };

  const [filters, setFilters] = useState<Filters>({
    focusArea: null,
    searchTerm: ''
  });

  const handleFilterChange = (newFilters: Filters) => {
    if (!equal(newFilters, filters)) {
      setFilters(newFilters);
    }
  };

  useEffect(() => {
    getGoalTemplates({
      variables: {
        archived: false,
        focusArea: filters.focusArea,
        journeyId,
        kind: GoalTemplateKindEnum.ALL,
        searchTerm: filters.searchTerm,
        sortBy: GoalTemplateSortByEnum.TITLE,
        sortDirection: SortDirectionEnum.ascending,
        usedInJourneyId: null
      }
    });
  }, [filters.focusArea, filters.searchTerm, getGoalTemplates, journeyId]);

  useEffect(() => {
    if (!getGoalTemplatesQuery.loading && getGoalTemplatesQuery.data?.goalTemplates.pageInfo.hasNextPage) {
      getGoalTemplatesQuery.fetchMore({
        variables: { cursor: getGoalTemplatesQuery.data?.goalTemplates.pageInfo.endCursor ?? '' }
      });
    }
  }, [getGoalTemplatesQuery]);

  useScrollPosition(
    ScrollPositionPageTypeEnum.GOAL_TEMPLATES,
    !getGoalTemplatesQuery.loading && !getGoalTemplatesQuery.data?.goalTemplates.pageInfo.hasNextPage
  );

  const allGoalTemplates = useNodesFromConnection(getGoalTemplatesQuery.data?.goalTemplates);
  const history = useHistory();

  const goalTemplatesByFocusArea = groupByFocusArea(allGoalTemplates);

  const searchGoalTemplates = useSearchGoalTemplates(id =>
    history.push(journeyRoute({ journeyId, linkAfterJourney: `/care-plan/goal-templates/${id}`, myClient }))
  );

  return (
    <div>
      <BackButton routerLink={journeyRoute({ journeyId, linkAfterJourney: '/care-plan', myClient })} />
      <div className={styles.header}>
        <div className={styles.headerItem}>
          <Heading1>
            <FormattedMessage id="pages.goalTemplates.introTitle" />
          </Heading1>

          <div className={styles.introSpacing}>
            <Heading2 bold={false}>
              <FormattedMessage id="pages.goalTemplates.introDescription" />
            </Heading2>
          </div>
        </div>
      </div>

      <HandleQuery query={getJourneyQuery}>
        {journeyData => (
          <>
            <GoalTemplateFilters filters={filters} onChange={handleFilterChange} onSearch={searchGoalTemplates} />
            <HandleQuery loadingContent={<GoalTemplatesLoader />} query={getGoalTemplatesQuery}>
              {_data => (
                <>
                  {Object.keys(goalTemplatesByFocusArea).map(focusArea => {
                    const goalTemplates = goalTemplatesByFocusArea[focusArea as FocusAreaEnum];
                    if (
                      goalTemplates &&
                      goalTemplates.length > 0 &&
                      (!filters.focusArea || filters.focusArea === focusArea)
                    ) {
                      return (
                        <FocusAreaSection
                          focusArea={focusArea as FocusAreaEnum}
                          goalTemplates={goalTemplates}
                          handleGoalCreate={handleGoalCreate}
                          journey={journeyData.journey}
                          key={focusArea}
                          loadingGoalTemplateId={loadingId}
                        />
                      );
                    }
                    return <div key={focusArea} />;
                  })}
                </>
              )}
            </HandleQuery>
          </>
        )}
      </HandleQuery>
    </div>
  );
};

export default GoalTemplatesLibrary;
